import * as React from "react";
import * as apiSettings from "../../../api/settings";
import { Alert, AlertType } from "../../../components/alert";
import { Button, ButtonType } from "../../../components/button";
import { CheckBox } from "../../../components/checkbox";
import { ComboBox, ComboBoxType, DropDownTextItem, DROPDOWN_TEXT_ITEM_HEIGHT } from "../../../components/comboBox";
import { FormGroup } from "../../../components/form";
import { GroupBox } from "../../../components/groupBox";
import { InputMask, InputSideButton, InputSize } from "../../../components/input";
import { Label, LabelColor, LabelContainer } from "../../../components/label";
import { Loader, LoaderStyle } from "../../../components/loader";
import { ScrollBarVisibility, ScrollViewer } from "../../../components/scrollViewer";
import { TextBox } from "../../../components/textBox";
import { InputValueType } from "../../../enumerations";
import * as globals from "../../../globals";
import { IWeekDay } from "../../../models";
import { IComboBoxValue } from "../../../models/form";
import { IServerSettings, ISetting, IStatus, ITemplate, IUser } from "../../../models/get";
import * as utils from "../../../utils/utils";
import SettingsNav from "../nav";
import LoginHeaderDialog from "./loginHeaderDialog";

interface IKeyValue
{
    key: string,
    value: string
}

interface IState
{
    isLoading?: boolean,
    currentUser?: IUser,
    weekDays?: IWeekDay[],
    eventFields?: IComboBoxValue[],
    dayViewTemplates?: IKeyValue[],
    monthViewTemplates?: IKeyValue[],
    status?: IStatus[],
    templates?: ITemplate[],
    form?: {
        daysHiddenInWorkweek?: number[],
        displayEventShowLabelInitials?: boolean,
        eventDialogChecks?: string,
        eventFields?: IComboBoxValue[],
        eventDVTemplate?: IComboBoxValue<string, string>,
        eventMVTemplate?: IComboBoxValue<string, string>,
        maxFilesizeAvatar?: number,
        notificationsEmailAcceptAllCertificates?: boolean,
        notificationsEmailActivated?: boolean,
        notificationsEmailFromAddress?: string,
        notificationsEmailFromName?: string,
        notificationsEmailSmtpPassword?: string,
        notificationsEmailSmtpPort?: number,
        notificationsEmailSmtpServer?: string,
        notificationsEmailSmtpUsername?: string,
        notificationsEmailUseSsl?: boolean,
        rulesCalendarsTimeGroups?: string,
        dataProtectionUrl?: string,
        termsOfUseUrl?: string,
        imprint?: string,
        loginHeaderImage?: string
    },
    isLoginHeaderDialogOpen?: boolean,
    errors?: Object,
    alertMessage?: string,
    alertType?: AlertType
}

export default class OtherSettings extends React.Component<{}, IState>
{
    private _settings: ISetting[] = [];
    private _serverSettings: IServerSettings = {};
    private _weekdays: IWeekDay[] = globals.getWeekDays();
    private _eventFields: IComboBoxValue[] = globals.getEventFields();

    /**
     * Erstellt eine neue Instanz der Klasse @see OtherSettings.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props: any)
    {
        super(props);

        this.state = {
            isLoading: true,
            weekDays: [],
            dayViewTemplates: [
                {
                    key: "EventType1",
                    value: "Typ 1"
                },
                {
                    key: "EventType2",
                    value: "Typ 2"
                }
            ],
            monthViewTemplates: [
                {
                    key: "EventType1_m",
                    value: "Typ 1"
                }
            ],
            form: {
                eventDVTemplate: {},
                eventMVTemplate: {}
            },
            errors: {}
        }

        this.initAsync();
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync()
    {
        this._settings = await globals.getSettingsAsync();
        this._serverSettings = await globals.getServerSettingsAsync();
        var currentUser: IUser = await globals.getCurrentUserAsync();
        var status: IStatus[] = await globals.getStatusAsync();
        var templates: ITemplate[] = await globals.getTemplatesAsync();

        this.setState((state: IState) =>
        {
            state.isLoading = false;

            state.currentUser = currentUser;
            state.status = status;
            state.templates = templates;
            state.form.daysHiddenInWorkweek = this.getSetting<number[]>(globals.SettingKey.daysHiddenInWorkweek);
            state.weekDays = globals.getWeekDays(state.form.daysHiddenInWorkweek);

            state.form.displayEventShowLabelInitials = this.getSetting<boolean>(globals.SettingKey.displayEventShowLabelInitials);
            state.form.eventDialogChecks = this.getSetting<string>(globals.SettingKey.eventDialogChecks);

            const eventFieldsFromSettings: string[] = this.getSetting<string[]>(globals.SettingKey.eventFields);
            const eventFieldsFromSettingsArray: IComboBoxValue[] = eventFieldsFromSettings.select(f => ({
                    selectedValue: f,
                    value: f
                }));

            state.eventFields = globals.getEventFields(eventFieldsFromSettingsArray);
            state.form.eventFields = eventFieldsFromSettingsArray;

            let dvTemplate = state.dayViewTemplates.where(t => t.key === this.getSetting<string>(globals.SettingKey.eventDVTemplate)).first();
            let mvTemplate = state.monthViewTemplates.where(t => t.key === this.getSetting<string>(globals.SettingKey.eventMVTemplate)).first();
            let report = state.templates.where(t => !t.is_list && t.name === this.getSetting<string>(globals.SettingKey.notificationsEmailSendOrderConfirmationReport)).first();
            let when = state.status.where(s => s.id === this.getSetting<number>(globals.SettingKey.notificationsEmailSendOrderConfirmationWhen)).first();

            state.form.eventDVTemplate = {
                value: dvTemplate.value,
                selectedValue: dvTemplate.key
            };

            state.form.eventMVTemplate = {
                value: mvTemplate.value,
                selectedValue: mvTemplate.key
            };

            state.form.maxFilesizeAvatar = this.getSetting<number>(globals.SettingKey.maxFilesizeAvatar);
            state.form.notificationsEmailAcceptAllCertificates = this.getSetting<boolean>(globals.SettingKey.notificationsEmailAcceptAllCertificates);
            state.form.notificationsEmailActivated = this.getSetting<boolean>(globals.SettingKey.notificationsEmailActivated);
            state.form.notificationsEmailFromAddress = this.getSetting<string>(globals.SettingKey.notificationsEmailFromAddress);
            state.form.notificationsEmailFromName = this.getSetting<string>(globals.SettingKey.notificationsEmailFromName);
            state.form.notificationsEmailSmtpPassword = this.getSetting<string>(globals.SettingKey.notificationsEmailSmtpPassword);
            state.form.notificationsEmailSmtpPort = this.getSetting<number>(globals.SettingKey.notificationsEmailSmtpPort);
            state.form.notificationsEmailSmtpServer = this.getSetting<string>(globals.SettingKey.notificationsEmailSmtpServer);
            state.form.notificationsEmailSmtpUsername = this.getSetting<string>(globals.SettingKey.notificationsEmailSmtpUsername);
            state.form.notificationsEmailUseSsl = this.getSetting<boolean>(globals.SettingKey.notificationsEmailUseSsl);
            state.form.eventDialogChecks = this.getSetting<string>(globals.SettingKey.eventDialogChecks);
            state.form.dataProtectionUrl = this.getSetting<string>(globals.SettingKey.dataProtectionUrl);
            state.form.termsOfUseUrl = this.getSetting<string>(globals.SettingKey.termsOfUseUrl);
            state.form.imprint = this.getSetting<string>(globals.SettingKey.imprint);
            state.form.loginHeaderImage = this.getSetting<string>(globals.SettingKey.loginHeaderImage);
            return state;
        });
    }

    /**
     * Gibt eine bestimmte Einstellung.
     * @param key Der Key/Name der Einstellung.
     */
    private getSetting<T>(key: globals.SettingKey): T
    {
        var setting = this._settings.where(s => s.id === key).first();
        return setting != null ? setting.settings : null;
    }

    private onSaveClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;

            await apiSettings.put({
                days_hidden_in_workweek: form.daysHiddenInWorkweek,
                event_day_view_template: form.eventDVTemplate.selectedValue,
                event_month_view_template: form.eventMVTemplate.selectedValue,
                showLabelInitials: form.displayEventShowLabelInitials,
                max_filesize_avatar: form.maxFilesizeAvatar,
                activated: form.notificationsEmailActivated,
                smtp_server: form.notificationsEmailSmtpServer,
                smtp_port: form.notificationsEmailSmtpPort,
                smtp_username: form.notificationsEmailSmtpUsername,
                smtp_password: form.notificationsEmailSmtpPassword,
                use_ssl: form.notificationsEmailUseSsl,
                accept_all_certificates: form.notificationsEmailAcceptAllCertificates,
                from_address: form.notificationsEmailFromAddress,
                from_name: form.notificationsEmailFromName,
                event_dialog_checks: form.eventDialogChecks,
                event_fields: form.eventFields ? form.eventFields.select(f => f.selectedValue) : [],
                data_protection_url: form.dataProtectionUrl,
                terms_of_use_url: form.termsOfUseUrl,
                imprint: form.imprint
            });

            this.setState({
                isLoading: false,
                errors: {},
                alertMessage: null
            });
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onLoginHeaderImageDeleteClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { form } = this.state;

            await apiSettings.deleteLoginHeader();
            this._settings = await globals.getSettingsAsync(true);

            this.setState((state: IState) =>
            {
                state.form.loginHeaderImage = this.getSetting<string>(globals.SettingKey.loginHeaderImage);

                state.isLoading = false;
                state.errors = {};
                state.alertMessage = null;
                return state;
            });
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer CheckBox ändert.
     * @param e Das auslösende Ereignis.
     */
    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) =>
    {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) =>
        {
            state.form[name] = value;
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert einer der Template ComboBoxen ändert.
     * @param selectedItem Das selektierte Template.
     * @param name Der Name des Form Elements.
     */
    private onTemplateChanged = (selectedItem: IKeyValue, name: string) =>
    {
        this.setState((state: IState) => 
        {
            state.form[name] = {
                value: selectedItem ? selectedItem.value : null,
                selectedValue: selectedItem ? selectedItem.key : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der WeekDay ComboBox ändert.
     * @param selectedItem Der selektierte Wochentag.
     */
    private onWeekDayChanged = (selectedItem: IWeekDay) =>
    {
        const { weekDays, form } = this.state;

        const newSelectedWeekDays: number[] = [];
        const newAvailableWeekDays: IWeekDay[] = [];

        if (form.daysHiddenInWorkweek && form.daysHiddenInWorkweek.length > 0)
            form.daysHiddenInWorkweek.forEach(d => newSelectedWeekDays.push(d));

        weekDays.forEach(d => newAvailableWeekDays.push({ ...d }));

        newSelectedWeekDays.push(selectedItem.key);
        newAvailableWeekDays.removeAt(d => d.key === selectedItem.key);

        newAvailableWeekDays.sort((a, b) => a.key - b.key);
        newSelectedWeekDays.sort((a, b) => a - b);

        this.setState((state: IState) => {
            state.weekDays = newAvailableWeekDays;
            state.form.daysHiddenInWorkweek = newSelectedWeekDays;
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der EventFields ComboBox ändert.
     * @param selectedItem Das selektierte Form Field.
     */
    private onEventFieldChanged = (selectedItem: IComboBoxValue) => {
        const { eventFields, form } = this.state;

        const newSelectedEventFields: IComboBoxValue[] = [];
        const newAvailableEventFields: IComboBoxValue[] = [];

        if (form.eventFields && form.eventFields.length > 0)
            form.eventFields.forEach(d => newSelectedEventFields.push(d));

        eventFields.forEach(d => newAvailableEventFields.push({ ...d }));

        newSelectedEventFields.push({ ...selectedItem });
        newAvailableEventFields.removeAt(d => d.selectedValue === selectedItem.selectedValue);

        newAvailableEventFields.sort((a, b) => {
            if (a.value < b.value) { return -1; }
            if (a.value > b.value) { return 1; }
            return 0;
        });
        newSelectedEventFields.sort((a, b) => {
            if (a.value < b.value) { return -1; }
            if (a.value > b.value) { return 1; }
            return 0;
        });

        this.setState((state: IState) => {
            state.eventFields = newAvailableEventFields;
            state.form.eventFields = newSelectedEventFields;
            return state;
        });
    }

    /**
     * Entfernt einen Wochentag aus der Auswahl.
     * @param day Der Tag der entfernt werden soll.
     */
    private onRemoveWeekDayClick = (day: IWeekDay) =>
    {
        const { weekDays, form } = this.state;

        const newSelectedWeekDays: number[] = [];
        const newAvailableWeekDays: IWeekDay[] = [];

        if (form.daysHiddenInWorkweek && form.daysHiddenInWorkweek.length > 0)
            form.daysHiddenInWorkweek.forEach(d => d !== day.key && newSelectedWeekDays.push(d));

        weekDays.forEach(d => newAvailableWeekDays.push({ ...d }));
        newAvailableWeekDays.push({ ...day });

        newAvailableWeekDays.sort((a, b) => a.key - b.key);
        newSelectedWeekDays.sort((a, b) => a - b);

        this.setState((state: IState) =>
        {
            state.weekDays = newAvailableWeekDays;
            state.form.daysHiddenInWorkweek = newSelectedWeekDays;
            return state;
        });
    }

    /**
     * Entfernt ein Form Field aus der Auswahl.
     * @param field Das Form Field das entfernt werden soll.
     */
    private onRemoveEventFieldClick = (field: IComboBoxValue) => {
        const { eventFields, form } = this.state;

        const newSelectedEventFields: IComboBoxValue[] = [];
        const newAvailableEventFields: IComboBoxValue[] = [];

        if (form.eventFields && form.eventFields.length > 0)
            form.eventFields.forEach(d => d.selectedValue !== field.selectedValue && newSelectedEventFields.push({ ...d }));

        eventFields.forEach(d => newAvailableEventFields.push({ ...d }));
        newAvailableEventFields.push({ ...field });

        newAvailableEventFields.sort((a, b) => {
            if (a.value < b.value) { return -1; }
            if (a.value > b.value) { return 1; }
            return 0;
        });
        newSelectedEventFields.sort((a, b) => {
            if (a.value < b.value) { return -1; }
            if (a.value > b.value) { return 1; }
            return 0;
        });

        this.setState((state: IState) => {
            state.eventFields = newAvailableEventFields;
            state.form.eventFields = newSelectedEventFields;
            return state;
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     */
    private onDialogClosed = () =>
    {
        globals.getSettingsAsync().then((settings) =>
        {
            this._settings = settings;
            this.setState((state: IState) =>
            {
                state.form.loginHeaderImage = this.getSetting<string>(globals.SettingKey.loginHeaderImage);
                state.isLoginHeaderDialogOpen = false;
                return state;
            });
        });
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var {
            isLoading,
            currentUser,
            weekDays,
            eventFields,
            dayViewTemplates,
            monthViewTemplates,
            status,
            templates,
            form,
            isLoginHeaderDialogOpen,
            errors,
            alertMessage,
            alertType
        } = this.state;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1} />
            );
        }

        const isAdmin: boolean = currentUser.group_name === "Administrator";

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="others" />
                    </div>
                    <div className="settings-content_alert">
                        <Alert
                            alertType={alertType}
                            isClosable={true}
                            isVisible={alertMessage != null}
                            onHide={() => this.setState({ alertMessage: null })}>
                            {alertMessage}
                        </Alert>
                    </div>
                    <div className="settings-others">
                        <div className="settings-others_content-wrapper">
                            <ScrollViewer
                                HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                <div className="settings-others_content">
                                    <GroupBox>
                                        <FormGroup>
                                            <ComboBox
                                                canRemoveSelection={true}
                                                label="Ausgeblendete Tage in der Arbeitswoche"
                                                dataSource={weekDays}
                                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                comboBoxType={ComboBoxType.dropDownList}
                                                onSelectionChanged={this.onWeekDayChanged}
                                                valueMember="key"
                                                displayMember="value"
                                                size={InputSize.normal}
                                                errorMessage={errors["Weekdays"]}>
                                                {
                                                    (item: IWeekDay) =>
                                                    {
                                                        return (
                                                            <DropDownTextItem
                                                                key={item.key}
                                                                value={item.key}
                                                                displayValue={item.value} />
                                                        );
                                                    }
                                                }
                                            </ComboBox>
                                            <LabelContainer>
                                                {
                                                    form.daysHiddenInWorkweek.map((data) =>
                                                    {
                                                        let day: IWeekDay = this._weekdays.where(d => d.key === data).first();
                                                        return (
                                                            <Label
                                                                dataSource={day}
                                                                text={day.value}
                                                                isButtonVisible={true}
                                                                color={LabelColor.red}
                                                                onButtonClick={this.onRemoveWeekDayClick} />
                                                        );
                                                    })
                                                }
                                            </LabelContainer>
                                        </FormGroup>
                                    </GroupBox>
                                    <GroupBox headerText="Darstellung">
                                        <FormGroup>
                                            <ComboBox
                                                style={{
                                                    marginBottom: 15
                                                }}
                                                canRemoveSelection={true}
                                                label="Template für die Termindarstellung"
                                                dataSource={dayViewTemplates}
                                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                comboBoxType={ComboBoxType.dropDownList}
                                                value={form.eventDVTemplate.value}
                                                selectedValue={form.eventDVTemplate.selectedValue}
                                                onSelectionChanged={data => this.onTemplateChanged(data, "eventDVTemplate")}
                                                valueMember="key"
                                                displayMember="value"
                                                size={InputSize.normal}>
                                                {
                                                    (item: IKeyValue) =>
                                                    {
                                                        return (
                                                            <DropDownTextItem
                                                                key={item.key}
                                                                value={item.key}
                                                                displayValue={item.value} />
                                                        );
                                                    }
                                                }
                                            </ComboBox>
                                            <ComboBox
                                                style={{
                                                    marginBottom: 20
                                                }}
                                                canRemoveSelection={true}
                                                label="Template für die Termindarstellung in der Monatsansicht"
                                                dataSource={monthViewTemplates}
                                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                comboBoxType={ComboBoxType.dropDownList}
                                                value={form.eventMVTemplate.value}
                                                selectedValue={form.eventMVTemplate.selectedValue}
                                                onSelectionChanged={data => this.onTemplateChanged(data, "eventMVTemplate")}
                                                valueMember="key"
                                                displayMember="value"
                                                size={InputSize.normal}>
                                                {
                                                    (item: IKeyValue) =>
                                                    {
                                                        return (
                                                            <DropDownTextItem
                                                                key={item.key}
                                                                value={item.key}
                                                                displayValue={item.value} />
                                                        );
                                                    }
                                                }
                                            </ComboBox>
                                            <CheckBox
                                                label="Labels bei Terminen via Kürzel darstellen"
                                                isChecked={form.displayEventShowLabelInitials}
                                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "displayEventShowLabelInitials")} />
                                        </FormGroup>
                                    </GroupBox>
                                    <GroupBox headerText="Accounts">
                                        <FormGroup>
                                            <TextBox
                                                label="Maximale Größe eines Profilbilds (in KB)"
                                                mask={InputMask.number}
                                                value={form.maxFilesizeAvatar && form.maxFilesizeAvatar.toString()}
                                                valueType={InputValueType.number}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "maxFilesizeAvatar")} />
                                        </FormGroup>
                                    </GroupBox>
                                    <GroupBox headerText="Benachrichtigungen">
                                        <FormGroup>
                                            <CheckBox
                                                label="E-Mail Benachrichtigung aktivieren"
                                                isChecked={form.notificationsEmailActivated}
                                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "notificationsEmailActivated")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Postausgangsserver"
                                                value={form.notificationsEmailSmtpServer}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailSmtpServer")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Port"
                                                mask={InputMask.number}
                                                value={form.notificationsEmailSmtpPort && form.notificationsEmailSmtpPort.toString()}
                                                valueType={InputValueType.number}
                                                isDisabled={!form.notificationsEmailActivated}
                                                errorMessage={errors["SmtpPort"]}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailSmtpPort")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Benutzername"
                                                value={form.notificationsEmailSmtpUsername}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailSmtpUsername")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Passwort"
                                                value={form.notificationsEmailSmtpPassword}
                                                isDisabled={!form.notificationsEmailActivated}
                                                isPassword={true}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailSmtpPassword")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Sender Name"
                                                value={form.notificationsEmailFromName}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailFromName")} />
                                            <TextBox
                                                style={{
                                                    marginLeft: 28,
                                                    marginBottom: 20
                                                }}
                                                label="Sender Adresse"
                                                value={form.notificationsEmailFromAddress}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "notificationsEmailFromAddress")} />
                                            <CheckBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="SSL Verwenden"
                                                isChecked={form.notificationsEmailUseSsl}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "notificationsEmailUseSsl")} />
                                            <CheckBox
                                                style={{
                                                    marginLeft: 28
                                                }}
                                                label="Alle Zertifikate akzeptieren"
                                                isChecked={form.notificationsEmailAcceptAllCertificates}
                                                isDisabled={!form.notificationsEmailActivated}
                                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "notificationsEmailAcceptAllCertificates")} />
                                        </FormGroup>
                                    </GroupBox>
                                    <GroupBox headerText="Login">
                                        <div className="custom-login">
                                            <label>Logo/Header der Anmeldemaske ändern</label>
                                            {
                                                form.loginHeaderImage &&
                                                <img className="custom-login__img" src={form.loginHeaderImage} />
                                            }
                                            {
                                                !form.loginHeaderImage &&
                                                <div className="custom-login__placeholder">
                                                    Eigenes Logo hinzufügen
                                                </div>
                                            }
                                            <div className="custom-login__actions">
                                                <Button
                                                    text="Bild auswählen"
                                                    type={ButtonType.default}
                                                    onClick={() => this.setState({ isLoginHeaderDialogOpen: true })} />
                                                {
                                                    form.loginHeaderImage &&
                                                    <Button
                                                        text="Bild löschen"
                                                        type={ButtonType.danger}
                                                        onClick={this.onLoginHeaderImageDeleteClick} />
                                                }
                                            </div>
                                        </div>
                                    </GroupBox>
                                    {
                                        currentUser.is_super_admin &&
                                        <GroupBox headerText="Regeln">
                                            <FormGroup>
                                                <TextBox
                                                    label="Name der benutzerdefinierten Regeln für Plausibilitätsprüfungen"
                                                    value={form.eventDialogChecks}
                                                    hint={<span>Wenn keine benutzerdefinierte Regel vorhanden ist, dann muss hier immer die Regel <b>DefaultRules</b> angegeben werden.</span>}
                                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "eventDialogChecks")} />

                                                <ComboBox
                                                    canRemoveSelection={true}
                                                    label="Anzuzeigende Felder für die Termin Eingabemaske"
                                                    dataSource={eventFields}
                                                    dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                                    comboBoxType={ComboBoxType.dropDownList}
                                                    onSelectionChanged={this.onEventFieldChanged}
                                                    valueMember="key"
                                                    displayMember="value"
                                                    size={InputSize.normal}
                                                    errorMessage={errors["EventFields"]}
                                                    isDisabled={!eventFields || eventFields.length === 0}>
                                                    {
                                                        (item: IComboBoxValue) => {
                                                            return (
                                                                <DropDownTextItem
                                                                    key={item.selectedValue}
                                                                    value={item.selectedValue}
                                                                    displayValue={item.value} />
                                                            );
                                                        }
                                                    }
                                                </ComboBox>
                                                <LabelContainer>
                                                    {
                                                        form.eventFields.map((data) => {
                                                            let field: IComboBoxValue = this._eventFields.where(d => d.selectedValue === data.selectedValue).first();
                                                            return (
                                                                <Label
                                                                    dataSource={field}
                                                                    text={field.value}
                                                                    isButtonVisible={true}
                                                                    color={LabelColor.green}
                                                                    onButtonClick={this.onRemoveEventFieldClick} />
                                                            );
                                                        })
                                                    }
                                                </LabelContainer>
                                            </FormGroup>
                                        </GroupBox>
                                    }
                                    {
                                        (this._serverSettings.Mode == null || this._serverSettings.Mode === "self-hosted" || currentUser.is_super_admin) &&
                                        <GroupBox headerText="Datenschutz">
                                            <FormGroup>
                                                <TextBox
                                                    label="Link zur Datenschutzerklärung"
                                                    value={form.dataProtectionUrl}
                                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "dataProtectionUrl")} />
                                                <TextBox
                                                    label="Link zu den Nutzungsbedingungen"
                                                    value={form.termsOfUseUrl}
                                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "termsOfUseUrl")} />
                                                <TextBox
                                                    label="Link zum Impressum"
                                                    value={form.imprint}
                                                    onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "imprint")} />
                                            </FormGroup>
                                        </GroupBox>
                                    }
                                </div>
                            </ScrollViewer>
                        </div>
                        <div className="settings-others_actions">
                            <Button
                                text="Speichern"
                                type={ButtonType.primary}
                                onClick={this.onSaveClick} />
                        </div>
                    </div>
                    {
                        isLoginHeaderDialogOpen &&
                        <LoginHeaderDialog onClose={this.onDialogClosed} />
                    }
                </div>
            </div>
        );
    }
}